import { useState, useEffect } from 'react';

interface HeroProps {
  currentRate: number;
  lastUpdate: Date;
}

const Hero = ({ currentRate, lastUpdate }: HeroProps) => {
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    setIsUpdating(true);
    const timer = setTimeout(() => setIsUpdating(false), 1000);
    return () => clearTimeout(timer);
  }, [currentRate]);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Denver'
    });
  };

  return (
    <div className="relative min-h-[70vh] flex items-center justify-center bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600">
      <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/30"></div>
      <img
        src="https://public.readdy.ai/ai/img_res/0afd3645ee46ab1edac170e7fbc7566a.jpg"
        alt="Peso Dollar Exchange Rate Background"
        className="absolute inset-0 w-full h-full object-cover opacity-20"
      />
      
      <div className="relative z-10 text-center text-white px-6 max-w-6xl mx-auto">
        <h1 className="text-5xl md:text-7xl font-bold mb-6">
          Live Peso Dollar Exchange Rate
        </h1>
        <p className="text-xl md:text-2xl text-emerald-100 mb-12 max-w-3xl mx-auto">
          Real-time USD to MXN conversion rates, best exchange locations in Ciudad Juárez, and expert currency tips
        </p>
        
        {/* Live Rate Display */}
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 mb-8 max-w-2xl mx-auto border border-white/20">
          <div className="text-center">
            <div className="text-sm text-emerald-200 mb-2 flex items-center justify-center gap-2">
              <div className={`w-3 h-3 rounded-full ${isUpdating ? 'bg-yellow-400 animate-pulse' : 'bg-green-400'}`}></div>
              LIVE RATE
            </div>
            <div className="text-6xl md:text-7xl font-bold mb-4">
              ${currentRate.toFixed(2)}
            </div>
            <div className="text-lg text-emerald-100">
              1 USD = {currentRate.toFixed(2)} MXN
            </div>
            <div className="text-sm text-emerald-200 mt-4">
              Last updated: {formatTime(lastUpdate)} MST
            </div>
          </div>
        </div>

        {/* Key Benefits */}
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <div className="w-16 h-16 bg-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-refresh-line text-2xl text-white"></i>
            </div>
            <h3 className="text-xl font-bold mb-2">Real-Time Updates</h3>
            <p className="text-emerald-100">
              Live exchange rates updated every 30 seconds for accurate currency conversion
            </p>
          </div>
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <div className="w-16 h-16 bg-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-map-pin-line text-2xl text-white"></i>
            </div>
            <h3 className="text-xl font-bold mb-2">Best Locations</h3>
            <p className="text-emerald-100">
              Find the most competitive exchange rates at trusted locations in Juárez
            </p>
          </div>
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <div className="w-16 h-16 bg-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-money-dollar-circle-line text-2xl text-white"></i>
            </div>
            <h3 className="text-xl font-bold mb-2">Save Money</h3>
            <p className="text-emerald-100">
              Expert tips to maximize your purchasing power and avoid hidden fees
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hero;